import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { menuIdForUrl as resolveMenuId } from './menu-url.utils';

export interface MenuItem {
    id: string;
    icon: string;
    hidden: boolean;
    link?: string;
    children?: {
        id: string;
        link: string;
        hidden: boolean;
        /** Additional URLs that also mark this item active, e.g. a page that opens on a tab with its own URL. */
        activeUrls?: string[];
    }[];
}

const MENU_ITEMS: MenuItem[] = [
    {
        id: 'bookLookup',
        icon: '/assets/icons/svgs/CollectionDiscover.svg',
        hidden: true, // Admin-only (PlatformStaff); shown via setUserMenuVisibility()
        link: '/pages/book'
    },
    {
        id: 'patronAndCommunity',
        icon: '/assets/icons/svgs/Patron.svg',
        hidden: false,
        children: [
            {
                id: 'patronDetail',
                link: '/pages/patron-and-community/detail',
                hidden: false
            },
            {
                id: 'patronCollectionUse',
                link: '/pages/patron-and-community/collection-use',
                hidden: false
            },
            {
                id: 'communityMap',
                link: '/pages/patron-and-community/community-map',
                hidden: false
            },
            {
                id: 'demographics',
                link: '/pages/patron-and-community/demographics',
                hidden: false
            }
        ]
    },
    {
        id: 'collectionAnalyze',
        icon: '/assets/icons/svgs/CollectionAnalyze.svg',
        hidden: false,
        children: [
            {
                id: 'physicalCollectionStats',
                link: '/pages/collectionStats',
                hidden: false
            },
            {
                id: 'physicalCollectionPerformance',
                link: '/pages/physicalCollectionPerformance',
                hidden: false,
                // The page opens on its tabs, which keep the URLs of the pages they replaced.
                activeUrls: [
                    '/pages/collectionCodes',
                    '/pages/collectionDewey',
                    '/pages/collectionBisac',
                    '/pages/collectionFindIt',
                    '/pages/collectionLCC'
                ]
            },
            {
                id: 'physicalCollectionCodes',
                link: '/pages/update-message/collectionCodes',
                hidden: false
            },
            {
                id: 'physicalCollectionByDewey',
                link: '/pages/update-message/collectionDewey',
                hidden: false
            },
            {
                id: 'physicalCollectionByBisac',
                link: '/pages/update-message/collectionBisac',
                hidden: false
            },
            {
                id: 'physicalCollectionByFindIt',
                link: '/pages/update-message/collectionFindIt',
                hidden: true
            },
            {
                id: 'physicalCollectionByLLC',
                link: '/pages/update-message/collectionLCC',
                hidden: true
            },
            {
                id: 'physicalCollectionByBranch',
                link: '/pages/collectionBranch',
                hidden: false
            },
            {
                id: 'physicalItemsAdded',
                link: '/pages/physicalItemsAdded',
                hidden: false
            },
            {
                id: 'pageFiltersTest',
                link: '/pages/page-filters-test',
                hidden: true
            }
        ]
    },
    {
        id: 'collectionCirculation',
        icon: '/assets/icons/svgs/CollectionCirculation.svg',
        hidden: false,
        children: [
            {
                id: 'physicalCircHolds',
                link: '/pages/physical-circ-holds',
                hidden: false
            },
            {
                id: 'physicalCircDetail',
                link: '/pages/circulationDetail',
                hidden: false
            },
            {
                id: 'physicalCircDetailByLocation',
                link: '/pages/locationCirculation',
                hidden: false
            },
            {
                id: 'circTracker',
                link: '/pages/circ-tracker',
                hidden: true
            },
            {
                id: 'physicalElectronicCirculation',
                link: '/pages/em/peCircCompared',
                hidden: false
            },
            {
                id: 'electronicCost',
                link: '/pages/em/cost',
                hidden: false
            },
            {
                id: 'electronicVendorFormats',
                link: '/pages/em/electronicByVendor',
                hidden: false
            }
        ]
    },
    {
        id: 'collectionDiscover',
        icon: '/assets/icons/svgs/CollectionDiscover.svg',
        hidden: false,
        children: [
            {
                id: 'buyNow',
                link: '/pages/inDemandTitles',
                hidden: true // Admin-only (PlatformStaff); shown via setUserMenuVisibility()
            },
            {
                id: 'trendingTitles',
                link: '/pages/trendingTitles',
                hidden: false
            },
            {
                id: 'bookLists',
                link: '/pages/bestSellers',
                hidden: false
            },
            {
                id: 'curatedLists',
                link: '/pages/curated-lists',
                hidden: false
            },
            {
                id: 'contributorsAndSeries',
                link: '/pages/contributorsAndSeries',
                hidden: false
            }
        ]
    },
    {
        id: 'diversity',
        icon: '/assets/icons/svgs/Diversity.svg',
        hidden: false,
        children: [
            {
                id: 'diversityDashboard',
                link: '/pages/diversity/dashboard',
                hidden: false
            },
            {
                id: 'diversityItemsAdded',
                link: '/pages/diversity/items-added',
                hidden: false
            },
            {
                id: 'diversityByBranch',
                link: '/pages/diversity/by-branch',
                hidden: true
            },
            {
                id: 'recommendedDiverseTitles',
                link: '/pages/diversity/recommended',
                hidden: false
            }
        ]
    },
    {
        id: 'collectionAcquire',
        icon: '/assets/icons/svgs/CollectionAcquire.svg',
        hidden: true,
        children: [
            {
                id: 'budgetSpending',
                link: '/pages/BudgetSpend',
                hidden: true
            },
            {
                id: 'titlesOrdered',
                link: '/pages/titlesOrdered',
                hidden: true
            },
            {
                id: 'openOrders',
                link: '/pages/openOrders',
                hidden: true
            },
            {
                id: 'invoices',
                link: '/pages/allInvoices',
                hidden: true
            }
        ]
    },
    {
        id: 'collectionMaintain',
        icon: '/assets/icons/svgs/CollectionMaintain.svg',
        hidden: false,
        children: [
            {
                id: 'reportBuilder',
                link: '/pages/ReportBuilder',
                hidden: false
            },
            {
                id: 'balance',
                link: '/pages/balance',
                hidden: false
            },
            {
                id: 'balanceReport',
                link: '/pages/balanceReport',
                hidden: false
            },
            {
                id: 'inventory',
                link: '/pages/inventory',
                hidden: true
            },
            {
                id: 'shelvingAllocation',
                link: '/pages/shelving-allocation',
                hidden: false
            }
        ]
    },
    {
        id: 'incidentReporting',
        icon: '/assets/icons/svgs/IncidentsReporting.svg',
        hidden: false,
        children: [
            {
                id: 'incidentReportingDashboard',
                link: '/pages/incidentReporting/dashboard',
                hidden: false
            },
            {
                id: 'incidentReports',
                link: '/pages/incidentReporting/reports',
                hidden: false
            },
            {
                id: 'incidentPimPeople',
                link: '/pages/incidentReporting/pim',
                hidden: false
            },
            {
                id: 'incidentSetupAdmin',
                link: '/pages/incidentReporting/setup',
                hidden: false
            }
        ]
    },
    {
        id: 'incidentPromo',
        icon: '/assets/icons/svgs/IncidentsReporting.svg',
        hidden: true,
        children: [
            {
                id: 'incidentReportingDashboard',
                link: '/pages/incidentReporting/promo',
                hidden: false
            }
        ]
    },
    {
        id: 'metrics',
        icon: '/assets/icons/svgs/Metrics.svg',
        hidden: false,
        children: [
            {
                id: 'metricsDashboard',
                link: '/pages/dashboard',
                hidden: false
            },
            {
                id: 'enterMetrics',
                link: '/pages/metricsEdit',
                hidden: true
            },
            {
                id: 'metricsAnalysis',
                link: '/pages/metrics-analysis',
                hidden: false
            },
            {
                id: 'programs',
                link: '/pages/programs',
                hidden: true
            },
            {
                id: 'footTraffic',
                link: '/pages/footTraffic',
                hidden: true
            }
        ]
    },
    {
        id: 'schedules',
        icon: '/assets/icons/svgs/Schedules.svg',
        hidden: true, // behind the 'scheduler' feature flag; see AuthService.setUserMenuVisibility()
        children: [
            {
                id: 'schedulerSetupAdmin',
                link: '/pages/schedules',
                hidden: false
            }
        ]
    },
    {
        id: 'fundingIntelligence',
        icon: '/assets/icons/svgs/FundingIntelligence.svg',
        hidden: true, // 'fundingIntelligence' feature flag + Platform Staff while testing; see AuthService.setUserMenuVisibility()
        children: [
            {
                id: 'fundingGrantFinder',
                link: '/pages/funding-intelligence/grants',
                hidden: false
            },
            {
                id: 'fundingGrantProfile',
                link: '/pages/funding-intelligence/profile',
                hidden: false
            },
            {
                id: 'fundingCatalogAdmin',
                link: '/pages/funding-intelligence/admin',
                hidden: true // Platform Staff or fundingAdmin only
            }
        ]
    },
    {
        id: 'actionPlan',
        icon: '/assets/icons/svgs/ActionPlan.svg',
        hidden: false,
        link: '/pages/cmPlan'
    },
    {
        id: 'metisDirector',
        icon: '/assets/icons/svgs/MetisDirector.svg',
        hidden: true, // Will be shown only for userid 102
        link: '/pages/metis-director'
    },
    {
        id: 'customReports',
        icon: '/assets/icons/svgs/CustomReports.svg',
        hidden: true,
        link: '/pages/customReports'
    },
    {
        id: 'notify',
        icon: '/assets/icons/svgs/Notify.svg',
        hidden: true,
        children: [
            {
                id: 'notifyDashboard',
                link: '/pages/notify/dashboard',
                hidden: false
            },
            {
                id: 'notifyTransactional',
                link: '/pages/notify/transactional',
                hidden: false
            },
            {
                id: 'notifyMarketing',
                link: '/pages/notify/marketing',
                hidden: false
            },
            {
                id: 'notifyReports',
                link: '/pages/notify/reports',
                hidden: false
            },
            {
                id: 'notifySettings',
                link: '/pages/notify/settings',
                hidden: false
            }
        ]
    }
];

const onlyIncidentReportingMenu: MenuItem[] = [
    {
        id: 'incidentReporting',
        icon: '/assets/icons/svgs/IncidentsReporting.svg',
        hidden: false,
        children: [
            {
                id: 'incidentReportingDashboard',
                link: '/pages/incidentReporting/dashboard',
                hidden: false
            },
            {
                id: 'incidentReports',
                link: '/pages/incidentReporting/reports',
                hidden: false
            },
            {
                id: 'incidentPimPeople',
                link: '/pages/incidentReporting/pim',
                hidden: false
            },
            {
                id: 'incidentSetupAdmin',
                link: '/pages/incidentReporting/setup',
                hidden: false
            }
        ]
    }
];

// Menu for Submitter-only users: Reports only (no Dashboard, People, or Setup)
const submitterOnlyMenu: MenuItem[] = [
    {
        id: 'incidentReporting',
        icon: '/assets/icons/svgs/IncidentsReporting.svg',
        hidden: false,
        children: [
            {
                id: 'incidentReports',
                link: '/pages/incidentReporting/reports',
                hidden: false
            }
        ]
    }
];

@Injectable({ providedIn: 'root' })
export class MenuService {
    favoritesToDisplay: any[] = [];
    currentFavorite: boolean;

    menu$ = new BehaviorSubject<MenuItem[]>([]);

    constructor() {
        this.refreshMenu();
    }

    refreshMenu(): void {
        this.menu$.next(JSON.parse(JSON.stringify(MENU_ITEMS)));
    }

    setOnlyIncidentReporting(): void {
        this.menu$.next(onlyIncidentReportingMenu);
    }

    setSubmitterOnlyMenu(): void {
        this.menu$.next(submitterOnlyMenu);
    }

    setMenuVisibility(id: string, show: boolean): void {
        const menu = this.menu$.value;
        menu.forEach((menuItem) => {
            if (menuItem.id === id) {
                menuItem.hidden = !show;
            }
            if (menuItem.children?.length > 0) {
                menuItem.children.forEach((childItem) => {
                    if (childItem.id === id) {
                        childItem.hidden = !show;
                    }
                });
            }
        });

        this.menu$.next(menu);
    }

    /** The sidebar entry a URL belongs to (longest matching link wins), or null — breadcrumb label source. */
    menuIdForUrl(url: string): string | null {
        return resolveMenuId(url, MENU_ITEMS);
    }

    getMenu(): MenuItem[] {
        // return this.menu;
        return this.menu$.value;
    }
}
